/* eslint-disable no-nested-ternary */
/**
 * By default, React DOM escapes any values embedded in JSX before rendering them,
 * but sometimes it is necessary to sanitize the user-generated content of received entities.
 * If you use this data in component props without any sanitization or encoding,
 * it might create XSS vulnerabilities.
 *
 * You should especially consider how you are using extended data inside the app.
 */

import pick from 'lodash/pick';
import omit from 'lodash/omit';
import isPlainObject from 'lodash/isPlainObject';

const ESCAPE_TEXT_REGEXP = /[<>]/g;
const ESCAPE_TEXT_REPLACEMENTS = {
  // fullwidth lesser-than character
  '<': '\uff1c',
  // fullwidth greater-than character
  '>': '\uff1e',
};

// An example how you could sanitize text content.
// This swaps some coding related characters to less dangerous ones
const sanitizeText = str =>
  str == null
    ? str
    : typeof str === 'string'
    ? str.replace(ESCAPE_TEXT_REGEXP, ch => ESCAPE_TEXT_REPLACEMENTS[ch])
    : '';

/**
 * Leaf paths of `profile.metadata` that other users' records may keep.
 * Whole-object allowlists are deliberately avoided: e.g. `allTimeStats` also holds revenue.
 * Anything not listed here (including unknown future keys) is dropped for other users.
 */
export const ALLOWED_OTHER_USER_METADATA_PATHS = [
  'membershipTier',
  'allTimeStats.responseRate',
  'allTimeStats.booking.accepted',
  'allTimeStatsAsCustomer.completedBookings',
  'metrics.responseTime.last30DaysAverageHours',
  'rating',
];

/**
 * Leaf paths of listing `metadata` (listing and ownListing) that are kept.
 */
export const ALLOWED_LISTING_METADATA_PATHS = ['rating'];

/**
 * `profile.publicData` fields that are removed from other users' records.
 * The signed-in user keeps them.
 */
export const OTHER_USER_STRIPPED_PUBLIC_DATA_KEYS = [
  'idVerificationUrl',
  'idVerificationStatus',
  'idVerificationTimeStamp',
  'waitingList',
  'joinDate',
];

const LEGACY_REVIEW_METADATA_KEYS = ['reviews', 'reviewsAsProvider', 'reviewsAsCustomer'];

const ADMIN_TIER = 'admin';
const PREMIUM_TIER = 'premium';

/**
 * Sanitize user entity.
 * If you add public data, you should probably sanitize it here.
 * By default, React DOM escapes any values embedded in JSX before rendering them,
 * but if you use this data on props, it might create XSS vulnerabilities
 * E.g. you should sanitize and encode URI if you are creating links from public data.
 */
export const sanitizeUser = (entity, isCurrentUser = false, { isSelf = false } = {}) => {
  // The signed-in user (type `currentUser`, or a `user` entity matched by id) keeps own metadata.
  const keepOwnData = isCurrentUser || isSelf;

  const { attributes, ...restEntity } = entity || {};
  const { profile, ...restAttributes } = attributes || {};
  const { bio, displayName, abbreviatedName, publicData, metadata, ...restProfile } = profile || {};

  // eslint-disable-next-line no-shadow
  const sanitizePublicData = publicData => {
    // TODO: If you add public data, you should probably sanitize it here.
    if (!publicData) {
      return {};
    }
    if (keepOwnData || !isPlainObject(publicData)) {
      return { publicData };
    }
    return { publicData: omit(publicData, OTHER_USER_STRIPPED_PUBLIC_DATA_KEYS) };
  };

  // eslint-disable-next-line no-shadow
  const sanitizeMetadata = metadata => {
    if (!metadata) {
      return {};
    }
    if (!isPlainObject(metadata)) {
      return { metadata: {} };
    }

    if (keepOwnData) {
      // Always strip legacy review metadata as it contains OTHER users' PII
      return { metadata: omit(metadata, LEGACY_REVIEW_METADATA_KEYS) };
    }

    const safeMetadata = pick(metadata, ALLOWED_OTHER_USER_METADATA_PATHS);
    // `admin` gates feature flags for the signed-in user only. Other users appear as `premium`
    // (hasPremiumMembership treats admin as premium, so badges render the same).
    if (safeMetadata.membershipTier === ADMIN_TIER) {
      safeMetadata.membershipTier = PREMIUM_TIER;
    }

    return { metadata: safeMetadata };
  };

  // Strip PII fields from attributes for non-currentUser entities
  const sanitizeAttributes = (attrs, isCurrentUser = false) => {
    if (!attrs) {
      return {};
    }

    if (isCurrentUser) {
      // currentUser is the signed-in user's own data - keep their fields intact
      return attrs;
    }

    const {
      email,
      emailVerified,
      pendingEmail,
      identityProviders,
      stripeConnected,
      permissions,
      ...safeAttributes
    } = attrs;

    // Keep banned and deleted for display purposes (shows "Banned user" / "Deleted user")
    return safeAttributes;
  };

  const profileMaybe = profile
    ? {
        profile: {
          // The signed-in user's own firstName, lastName, protectedData and privateData
          // are needed by account settings. They are never returned for other users.
          ...(isCurrentUser ? restProfile : {}),
          abbreviatedName: sanitizeText(abbreviatedName),
          displayName: sanitizeText(displayName),
          bio,
          ...sanitizePublicData(publicData),
          ...sanitizeMetadata(metadata),
        },
      }
    : {};
  const sanitizedRestAttributes = sanitizeAttributes(restAttributes, isCurrentUser);
  const attributesMaybe = attributes
    ? { attributes: { ...profileMaybe, ...sanitizedRestAttributes } }
    : {};

  return { ...attributesMaybe, ...restEntity };
};

/**
 * Sanitize listing entity.
 * If you add public data, you should probably sanitize it here.
 * By default, React DOM escapes any values embedded in JSX before rendering them,
 * but if you use this data on props, it might create XSS vulnerabilities
 * E.g. you should sanitize and encode URI if you are creating links from public data.
 */
export const sanitizeListing = (entity, isOwnListing = false) => {
  const { attributes, ...restEntity } = entity;
  const { title, publicData, metadata, ...restAttributes } = attributes || {};

  const sanitizeLocation = location => {
    const { address, usState, usCounty, game, acreage } = location || {};
    return {
      address: sanitizeText(address),
      usState: sanitizeText(usState),
      usCounty: sanitizeText(usCounty),
      game: sanitizeText(game),
      acreage: sanitizeText(acreage),
    };
  };

  // eslint-disable-next-line no-shadow
  const sanitizePublicData = (publicData, isOwnListing) => {
    if (!publicData) {
      return {};
    }

    if (isOwnListing) {
      // ownListing needs full location data for the listing edit wizard
      return { publicData };
    }

    // For public listings, sanitize location and rules
    const { location, rules, ...restPublicData } = publicData;
    const locationMaybe = location ? { location: sanitizeLocation(location) } : {};
    const rulesMaybe = rules ? { rules: sanitizeText(rules) } : {};

    return { publicData: { ...locationMaybe, ...rulesMaybe, ...restPublicData } };
  };

  // eslint-disable-next-line no-shadow
  const sanitizeMetadata = metadata => {
    if (!metadata) {
      return {};
    }
    if (!isPlainObject(metadata)) {
      return { metadata: {} };
    }

    return { metadata: pick(metadata, ALLOWED_LISTING_METADATA_PATHS) };
  };

  const attributesMaybe = attributes
    ? {
        attributes: {
          title: sanitizeText(title),
          ...sanitizePublicData(publicData, isOwnListing),
          ...sanitizeMetadata(metadata),
          ...restAttributes,
        },
      }
    : {};

  return { ...attributesMaybe, ...restEntity };
};

/**
 * Whether the given `user` entity is the signed-in user. Never true on the server, so that
 * server-rendered state (window.__PRELOADED_STATE__) only ever contains the strict allowlist.
 */
const isSignedInUser = (entity, currentUserId) =>
  typeof window !== 'undefined' && !!currentUserId && entity?.id?.uuid === currentUserId;

/**
 * Sanitize entities if needed.
 * Remember to add your own sanitization rules for your extended data
 */
export const sanitizeEntity = (entity, { currentUserId } = {}) => {
  const { type } = entity;
  switch (type) {
    case 'listing':
      return sanitizeListing(entity, false);
    case 'ownListing':
      return sanitizeListing(entity, true);
    case 'user':
      return sanitizeUser(entity, false, { isSelf: isSignedInUser(entity, currentUserId) });
    case 'currentUser':
      return sanitizeUser(entity, true);
    default:
      return entity;
  }
};
